import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { clientesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Clientes = () => {
  const navigate = useNavigate();
  const { modoEspectador } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showDetalle, setShowDetalle] = useState(false);
  const [clienteDetalle, setClienteDetalle] = useState(null);
  // [Buscador unificado] Resolver por N° de orden física o cualquier código del sistema
  const [ordenQuery, setOrdenQuery] = useState('');
  const [ordenResultados, setOrdenResultados] = useState(null);
  const [buscandoOrden, setBuscandoOrden] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({
    tipo_persona: 'natural', tipo_identificacion: 'cedula', identificacion: '',
    nombres: '', apellidos: '', razon_social: '', nombre_comercial: '', representante_legal: '',
    fecha_nacimiento: '', genero: '', telefono: '', celular: '', email: '', whatsapp: '',
    direccion: '', ciudad: '', provincia: '', ocupacion: '', actividad: '', hobbie: '',
    horas_computador: '', horas_celular: '', usa_lentes: false,
    tipo_cliente: 'regular', convenio_empresa: '', limite_credito: '', notas: ''
  });
  const [ciValido, setCiValido] = useState(null);
  const [debouncedBuscar, setDebouncedBuscar] = useState(buscar);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBuscar(buscar), 400);
    return () => clearTimeout(timer);
  }, [buscar]);

  const cargarClientes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clientesAPI.getAll({ buscar: debouncedBuscar, page, limit: 25 });
      if (res.data.success) {
        setClientes(res.data.data);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
    setLoading(false);
  }, [debouncedBuscar, page]);

  useEffect(() => { cargarClientes(); }, [cargarClientes]);

  const resetForm = () => {
    setForm({
      tipo_persona: 'natural', tipo_identificacion: 'cedula', identificacion: '',
      nombres: '', apellidos: '', razon_social: '', nombre_comercial: '', representante_legal: '',
      fecha_nacimiento: '', genero: '', telefono: '', celular: '', email: '', whatsapp: '',
      direccion: '', ciudad: '', provincia: '', ocupacion: '', actividad: '', hobbie: '',
      horas_computador: '', horas_celular: '', usa_lentes: false,
      tipo_cliente: 'regular', convenio_empresa: '', limite_credito: '', notas: ''
    });
    setCiValido(null);
    setEditando(null);
  };

  // FIX: Reset all form state when closing modal to prevent stale data
  const handleCloseModal = () => {
    setShowModal(false);
    setEditando(null);
    resetForm();
  };

  const abrirModal = (cliente = null) => {
    if (cliente) {
      setEditando(cliente.id);
      setForm({
        ...cliente,
        tipo_persona: cliente.tipo_persona || 'natural',
        razon_social: cliente.razon_social || '',
        nombre_comercial: cliente.nombre_comercial || '',
        representante_legal: cliente.representante_legal || '',
        fecha_nacimiento: cliente.fecha_nacimiento ? cliente.fecha_nacimiento.split('T')[0] : '',
        horas_computador: cliente.horas_computador || '',
        horas_celular: cliente.horas_celular || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const validarCI = async () => {
    if (!form.identificacion) return;
    try {
      const res = await clientesAPI.validarCI({ identificacion: form.identificacion, tipo_identificacion: form.tipo_identificacion });
      setCiValido(res.data.data);
      if (res.data.data.existe && !editando) {
        toast.warning('Ya existe un cliente con esa identificación');
      }
    } catch (error) {
      setCiValido(null);
    }
  };

  // FIX: Validate CI on submit — prevent saving if identification is invalid
  const guardar = async (e) => {
    e.preventDefault();
    if (form.identificacion && ciValido && !ciValido.valido) {
      toast.error('La identificación ingresada no es válida');
      return;
    }
    if (form.identificacion && ciValido && ciValido.existe && !editando) {
      toast.error('Ya existe un cliente con esa identificación');
      return;
    }
    try {
      if (editando) {
        await clientesAPI.update(editando, form);
        toast.success('Cliente actualizado');
      } else {
        await clientesAPI.create(form);
        toast.success('Cliente creado');
      }
      setShowModal(false);
      resetForm();
      cargarClientes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  const verDetalle = async (id) => {
    try {
      const res = await clientesAPI.getById(id);
      if (res.data.success) {
        setClienteDetalle(res.data.data);
        setShowDetalle(true);
      }
    } catch (error) {
      toast.error('Error al cargar detalle');
    }
  };

  const calcularEdad = (fechaNac) => {
    if (!fechaNac) return '-';
    const hoy = new Date();
    const nac = new Date(fechaNac);
    let edad = hoy.getFullYear() - nac.getFullYear();
    if (hoy.getMonth() < nac.getMonth() || (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())) edad--;
    return `${edad} años`;
  };

  const buscarPorOrden = async () => {
    const q = ordenQuery.trim();
    if (q.length < 2) { toast.info('Escribe al menos 2 caracteres'); return; }
    setBuscandoOrden(true);
    try {
      const res = await clientesAPI.buscarOrden(q);
      setOrdenResultados(res.data.success ? res.data.data : []);
    } catch { toast.error('Error al buscar la orden'); setOrdenResultados([]); }
    setBuscandoOrden(false);
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          {/* FIX: Responsive search — use w-100 with maxWidth instead of fixed width */}
          <input
            type="text"
            className="form-control w-100"
            placeholder="Buscar por CI, nombre, teléfono..."
            value={buscar}
            onChange={(e) => { setBuscar(e.target.value); setPage(1); }}
            style={{ maxWidth: '300px' }}
          />
        </div>
        <button className="btn btn-primary" onClick={() => abrirModal()} disabled={modoEspectador} title={modoEspectador ? 'No disponible en modo espectador' : ''}>
          <i className="bi bi-plus-lg me-2"></i>Nuevo Cliente
        </button>
      </div>

      {/* [Buscador unificado] Seguimiento por N° de orden física o cualquier código */}
      <div className="card border-0 shadow-sm mb-3" style={{ borderLeft: '4px solid #0d6efd' }}>
        <div className="card-body py-2">
          <label className="form-label small fw-bold mb-1"><i className="bi bi-search me-1"></i>Seguimiento por N° de orden</label>
          <div className="input-group input-group-sm">
            <input type="text" className="form-control" placeholder="N° de orden física, VTA-, OT-, ATE- o N° de factura SRI..."
              value={ordenQuery} onChange={e => setOrdenQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') buscarPorOrden(); }} />
            <button className="btn btn-primary" onClick={buscarPorOrden} disabled={buscandoOrden}>
              {buscandoOrden ? 'Buscando...' : 'Buscar'}
            </button>
            {ordenResultados !== null && (
              <button className="btn btn-outline-secondary" onClick={() => { setOrdenResultados(null); setOrdenQuery(''); }}>Limpiar</button>
            )}
          </div>
          {ordenResultados !== null && (
            <div className="mt-2">
              {ordenResultados.length === 0 ? (
                <div className="text-muted small">Sin resultados para ese código.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light"><tr><th>Tipo</th><th>Código</th><th>N° Orden</th><th>Cliente</th><th>Fecha</th><th></th></tr></thead>
                    <tbody>
                      {ordenResultados.map((r, i) => (
                        <tr key={i}>
                          <td><span className="badge bg-secondary text-uppercase">{r.tipo === 'orden_trabajo' ? 'orden' : r.tipo}</span></td>
                          <td className="fw-bold">{r.codigo_sistema}</td>
                          <td className="fw-bold text-primary">{r.numero_orden || '—'}</td>
                          <td>{r.cliente_nombre}</td>
                          <td>{r.fecha ? new Date(r.fecha).toLocaleDateString('es-EC') : '—'}</td>
                          <td className="text-end">
                            {r.cliente_id
                              ? <button className="btn btn-sm btn-primary" onClick={() => verDetalle(r.cliente_id)}><i className="bi bi-person-vcard me-1"></i>Ficha del paciente</button>
                              : <span className="text-muted small">Consumidor final</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Código</th>
                <th>Identificación</th>
                <th>Nombre</th>
                <th>Celular</th>
                <th>Email</th>
                <th>Tipo</th>
                <th>Consultas</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary"></div> Cargando...
                </td></tr>
              ) : clientes.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-4 text-muted">
                  <i className="bi bi-people fs-1 d-block mb-2 opacity-50"></i>No hay clientes
                </td></tr>
              ) : clientes.map(c => (
                <tr key={c.id}>
                  <td><span className="badge bg-light text-dark">{c.codigo}</span></td>
                  <td>
                    <small className="text-muted">{c.tipo_identificacion?.toUpperCase()}</small><br/>
                    {c.identificacion}
                  </td>
                  <td>
                    {c.tipo_persona === 'juridica' ? (
                      <>
                        <div className="fw-semibold">{c.razon_social || c.nombres}</div>
                        {c.nombre_comercial && <small className="text-muted">{c.nombre_comercial}</small>}
                        <span className="badge bg-info bg-opacity-10 text-info ms-1" style={{fontSize: '0.65rem'}}>Jurídica</span>
                      </>
                    ) : (
                      <>
                        <div className="fw-semibold">{c.nombres} {c.apellidos}</div>
                        {c.ocupacion && <small className="text-muted">{c.ocupacion}</small>}
                      </>
                    )}
                  </td>
                  <td>{c.celular || c.telefono || '-'}</td>
                  <td>{c.email || '-'}</td>
                  <td>
                    <span className={`badge ${c.tipo_cliente === 'vip' ? 'bg-warning text-dark' : c.tipo_cliente === 'corporativo' ? 'bg-info' : 'bg-secondary'}`}>
                      {c.tipo_cliente}
                    </span>
                  </td>
                  <td>
                    <span className="badge bg-primary bg-opacity-10 text-primary">{c.total_consultas || 0}</span>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-primary" onClick={() => verDetalle(c.id)} title="Ver detalle">
                        <i className="bi bi-eye"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => abrirModal(c)} title="Editar" disabled={modoEspectador}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-success" onClick={() => navigate(`/historia-clinica/nueva?cliente=${c.id}`)} title="Nueva HC">
                        <i className="bi bi-file-medical"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="card-footer d-flex justify-content-center">
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => p - 1)}>Anterior</button>
                </li>
                <li className="page-item active"><span className="page-link">{page} / {totalPages}</span></li>
                <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => p + 1)}>Siguiente</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editando ? 'Editar Cliente' : 'Nuevo Cliente'}</h5>
                <button className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <form onSubmit={guardar}>
                <div className="modal-body">
                  {/* Tipo de Persona */}
                  <div className="d-flex gap-3 mb-4">
                    <div className="form-check form-check-inline">
                      <input className="form-check-input" type="radio" name="tipoPersona" id="pNatural" value="natural"
                        checked={form.tipo_persona === 'natural'}
                        onChange={() => setForm({...form, tipo_persona: 'natural', tipo_identificacion: 'cedula'})}
                        disabled={!!editando}
                      />
                      <label className="form-check-label fw-semibold" htmlFor="pNatural">
                        <i className="bi bi-person me-1"></i>Persona Natural
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input className="form-check-input" type="radio" name="tipoPersona" id="pJuridica" value="juridica"
                        checked={form.tipo_persona === 'juridica'}
                        onChange={() => setForm({...form, tipo_persona: 'juridica', tipo_identificacion: 'ruc'})}
                        disabled={!!editando}
                      />
                      <label className="form-check-label fw-semibold" htmlFor="pJuridica">
                        <i className="bi bi-building me-1"></i>Persona Jurídica
                      </label>
                    </div>
                  </div>

                  {/* Identificación */}
                  <h6 className="text-primary mb-3"><i className="bi bi-person-vcard me-2"></i>Identificación</h6>
                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <label className="form-label">Tipo Doc.</label>
                      <select className="form-select" value={form.tipo_identificacion} onChange={e => setForm({...form, tipo_identificacion: e.target.value})}>
                        {form.tipo_persona === 'natural' ? (
                          <>
                            <option value="cedula">Cédula</option>
                            <option value="ruc">RUC</option>
                            <option value="pasaporte">Pasaporte</option>
                          </>
                        ) : (
                          <option value="ruc">RUC</option>
                        )}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Número *</label>
                      <div className="input-group">
                        <input type="text" className={`form-control ${ciValido ? (ciValido.valido ? 'is-valid' : 'is-invalid') : ''}`}
                          value={form.identificacion}
                          onChange={e => setForm({...form, identificacion: e.target.value.replace(/[^a-zA-Z0-9]/g, '')})}
                          onBlur={validarCI} required
                          maxLength={form.tipo_identificacion === 'ruc' ? 13 : form.tipo_identificacion === 'cedula' ? 10 : 20}
                          placeholder={form.tipo_identificacion === 'cedula' ? '10 dígitos' : form.tipo_identificacion === 'ruc' ? '13 dígitos' : 'Pasaporte'}
                        />
                        {ciValido && !ciValido.valido && <div className="invalid-feedback">Identificación inválida</div>}
                      </div>
                      {editando && <small className="text-muted d-block mt-1"><i className="bi bi-pencil-square me-1"></i>Puedes corregir la identificación si fue mal ingresada.</small>}
                    </div>
                    <div className="col-md-5">
                      <label className="form-label">Tipo Cliente</label>
                      <select className="form-select" value={form.tipo_cliente} onChange={e => setForm({...form, tipo_cliente: e.target.value})}>
                        <option value="regular">Regular</option>
                        <option value="frecuente">Frecuente</option>
                        <option value="corporativo">Corporativo</option>
                        <option value="vip">VIP</option>
                      </select>
                    </div>
                  </div>

                  {/* Datos según tipo de persona */}
                  {form.tipo_persona === 'natural' ? (
                    <>
                      <h6 className="text-primary mb-3"><i className="bi bi-person me-2"></i>Datos Personales</h6>
                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <label className="form-label">Nombres *</label>
                          <input type="text" className="form-control" value={form.nombres} onChange={e => setForm({...form, nombres: e.target.value})} required />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Apellidos *</label>
                          <input type="text" className="form-control" value={form.apellidos} onChange={e => setForm({...form, apellidos: e.target.value})} required />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Fecha Nacimiento</label>
                          <input type="date" className="form-control" value={form.fecha_nacimiento} onChange={e => setForm({...form, fecha_nacimiento: e.target.value})} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Género</label>
                          <select className="form-select" value={form.genero || ''} onChange={e => setForm({...form, genero: e.target.value})}>
                            <option value="">Seleccionar</option>
                            <option value="masculino">Masculino</option>
                            <option value="femenino">Femenino</option>
                            <option value="otro">Otro</option>
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Ocupación</label>
                          <input type="text" className="form-control" value={form.ocupacion || ''} onChange={e => setForm({...form, ocupacion: e.target.value})} />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <h6 className="text-primary mb-3"><i className="bi bi-building me-2"></i>Datos de la Empresa</h6>
                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <label className="form-label">Razón Social *</label>
                          <input type="text" className="form-control" value={form.razon_social || ''} onChange={e => setForm({...form, razon_social: e.target.value})} required placeholder="Nombre legal de la empresa" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Nombre Comercial</label>
                          <input type="text" className="form-control" value={form.nombre_comercial || ''} onChange={e => setForm({...form, nombre_comercial: e.target.value})} placeholder="Nombre de fantasía (opcional)" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Representante Legal</label>
                          <input type="text" className="form-control" value={form.representante_legal || ''} onChange={e => setForm({...form, representante_legal: e.target.value})} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Actividad Económica</label>
                          <input type="text" className="form-control" value={form.actividad || ''} onChange={e => setForm({...form, actividad: e.target.value})} />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Contacto */}
                  <h6 className="text-primary mb-3"><i className="bi bi-telephone me-2"></i>Contacto</h6>
                  <div className="row g-3 mb-4">
                    <div className="col-md-4">
                      <label className="form-label">Celular</label>
                      <input type="text" className="form-control" value={form.celular || ''} onChange={e => setForm({...form, celular: e.target.value})} placeholder="09XXXXXXXX" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Teléfono</label>
                      <input type="text" className="form-control" value={form.telefono || ''} onChange={e => setForm({...form, telefono: e.target.value})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">WhatsApp</label>
                      <input type="text" className="form-control" value={form.whatsapp || ''} onChange={e => setForm({...form, whatsapp: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-control" value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Dirección</label>
                      <input type="text" className="form-control" value={form.direccion || ''} onChange={e => setForm({...form, direccion: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Ciudad</label>
                      <input type="text" className="form-control" value={form.ciudad || ''} onChange={e => setForm({...form, ciudad: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Provincia</label>
                      <input type="text" className="form-control" value={form.provincia || ''} onChange={e => setForm({...form, provincia: e.target.value})} />
                    </div>
                  </div>

                  {/* Visual - solo para persona natural */}
                  {form.tipo_persona === 'natural' && (
                    <>
                      <h6 className="text-primary mb-3"><i className="bi bi-eye me-2"></i>Datos Visuales</h6>
                      <div className="row g-3 mb-4">
                        <div className="col-md-3">
                          <div className="form-check mt-4">
                            <input type="checkbox" className="form-check-input" checked={form.usa_lentes} onChange={e => setForm({...form, usa_lentes: e.target.checked})} id="usaLentes" />
                            <label className="form-check-label" htmlFor="usaLentes">Usa lentes</label>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Hrs. computador</label>
                          <input type="number" step="0.5" className="form-control" value={form.horas_computador || ''} onChange={e => setForm({...form, horas_computador: e.target.value})} />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Hrs. celular</label>
                          <input type="number" step="0.5" className="form-control" value={form.horas_celular || ''} onChange={e => setForm({...form, horas_celular: e.target.value})} />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Hobby</label>
                          <input type="text" className="form-control" value={form.hobbie || ''} onChange={e => setForm({...form, hobbie: e.target.value})} />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Notas */}
                  <div className="mb-3">
                    <label className="form-label">Notas</label>
                    <textarea className="form-control" rows="2" value={form.notas || ''} onChange={e => setForm({...form, notas: e.target.value})}></textarea>
                  </div>
                  {form.tipo_cliente === 'corporativo' && (
                    <div className="mb-3">
                      <label className="form-label">Empresa / Convenio</label>
                      <input type="text" className="form-control" value={form.convenio_empresa || ''} onChange={e => setForm({...form, convenio_empresa: e.target.value})} />
                    </div>
                  )}
                  {/* Límite de crédito */}
                  <div className="mb-3">
                    <label className="form-label"><i className="bi bi-wallet2 me-1"></i>Límite de Crédito</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input type="number" className="form-control" min="0" step="0.01"
                        value={form.limite_credito || ''} onChange={e => setForm({...form, limite_credito: e.target.value})}
                        placeholder="0 = sin límite" />
                    </div>
                    <small className="text-muted">Dejar en 0 o vacío para crédito ilimitado</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancelar</button>
                  <button type="submit" className="btn btn-primary">
                    <i className={`bi ${editando ? 'bi-check-lg' : 'bi-plus-lg'} me-2`}></i>
                    {editando ? 'Guardar Cambios' : 'Crear Cliente'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle */}
      {showDetalle && clienteDetalle && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`bi ${clienteDetalle.tipo_persona === 'juridica' ? 'bi-building' : 'bi-person-circle'} me-2`}></i>
                  {clienteDetalle.tipo_persona === 'juridica'
                    ? (clienteDetalle.razon_social || clienteDetalle.nombres)
                    : `${clienteDetalle.nombres} ${clienteDetalle.apellidos || ''}`
                  }
                </h5>
                <button className="btn-close" onClick={() => setShowDetalle(false)}></button>
              </div>
              <div className="modal-body">
                {/* Info principal */}
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <small className="text-muted">Identificación</small>
                    <div className="fw-bold">{clienteDetalle.tipo_identificacion?.toUpperCase()}: {clienteDetalle.identificacion}</div>
                  </div>
                  {clienteDetalle.tipo_persona === 'juridica' ? (
                    <>
                      <div className="col-md-4">
                        <small className="text-muted">Nombre Comercial</small>
                        <div className="fw-bold">{clienteDetalle.nombre_comercial || '-'}</div>
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted">Representante Legal</small>
                        <div className="fw-bold">{clienteDetalle.representante_legal || '-'}</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="col-md-4">
                        <small className="text-muted">Edad</small>
                        <div className="fw-bold">{calcularEdad(clienteDetalle.fecha_nacimiento)}</div>
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted">Usa lentes</small>
                        <div>{clienteDetalle.usa_lentes ? 'Sí' : 'No'}</div>
                      </div>
                    </>
                  )}
                  <div className="col-md-4">
                    <small className="text-muted">Celular</small>
                    <div className="fw-bold">{clienteDetalle.celular || '-'}</div>
                  </div>
                  <div className="col-md-4">
                    <small className="text-muted">Email</small>
                    <div>{clienteDetalle.email || '-'}</div>
                  </div>
                  <div className="col-md-4">
                    <small className="text-muted">{clienteDetalle.tipo_persona === 'juridica' ? 'Actividad' : 'Ocupación'}</small>
                    <div>{clienteDetalle.tipo_persona === 'juridica' ? (clienteDetalle.actividad || '-') : (clienteDetalle.ocupacion || '-')}</div>
                  </div>
                </div>

                {/* Limite de Credito */}
                {parseFloat(clienteDetalle.limite_credito) > 0 && (
                  <div className="alert alert-warning py-2 mb-3">
                    <i className="bi bi-wallet2 me-2"></i>
                    {/* FIX: null safety — toFixed on possibly NaN */}
                    <strong>Límite de crédito:</strong> ${(parseFloat(clienteDetalle.limite_credito) || 0).toFixed(2)}
                  </div>
                )}

                {/* Historias Clinicas */}
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  <i className="bi bi-file-medical me-2"></i>
                  Historias Clínicas ({clienteDetalle.historias_clinicas?.length || 0})
                </h6>
                {clienteDetalle.historias_clinicas?.length > 0 ? (
                  <div className="table-responsive mb-4">
                    <table className="table table-sm">
                      <thead>
                        <tr><th>Código</th><th>Fecha</th><th>Motivo</th><th>Diagnóstico</th><th>Optometrista</th><th></th></tr>
                      </thead>
                      <tbody>
                        {(clienteDetalle.historias_clinicas || []).map(hc => (
                          <tr key={hc.id}>
                            <td><span className="badge bg-light text-dark">{hc.codigo}</span></td>
                            <td>{hc.fecha_consulta ? new Date(hc.fecha_consulta).toLocaleDateString('es-EC') : '—'}</td>
                            <td>{hc.motivo_consulta?.substring(0, 40) || '-'}</td>
                            <td>{hc.diagnostico?.substring(0, 40) || '-'}</td>
                            <td>{hc.optometrista}</td>
                            <td>
                              <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/historia-clinica/${hc.id}`)}>
                                <i className="bi bi-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted mb-4">Sin historias clínicas registradas</p>
                )}

                {/* Prescripciones */}
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  <i className="bi bi-prescription2 me-2"></i>
                  Prescripciones ({clienteDetalle.prescripciones?.length || 0})
                </h6>
                {clienteDetalle.prescripciones?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr><th>Código</th><th>Fecha</th><th>OD (ESF/CYL/EJE)</th><th>OI (ESF/CYL/EJE)</th><th>Estado</th></tr>
                      </thead>
                      <tbody>
                        {(clienteDetalle.prescripciones || []).map(rx => (
                          <tr key={rx.id}>
                            <td><span className="badge bg-light text-dark">{rx.codigo}</span></td>
                            <td>{rx.fecha_emision ? new Date(rx.fecha_emision).toLocaleDateString('es-EC') : '—'}</td>
                            <td>{rx.od_esfera || '-'} / {rx.od_cilindro || '-'} / {rx.od_eje || '-'}°</td>
                            <td>{rx.oi_esfera || '-'} / {rx.oi_cilindro || '-'} / {rx.oi_eje || '-'}°</td>
                            <td><span className={`badge ${rx.estado === 'vigente' ? 'bg-success' : 'bg-secondary'}`}>{rx.estado}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted">Sin prescripciones registradas</p>
                )}

                {/* [Ficha 360] Saldo por cobrar */}
                {clienteDetalle.saldo_cxc && parseFloat(clienteDetalle.saldo_cxc.saldo_total) > 0 && (
                  <div className="alert alert-warning py-2 mb-3">
                    <i className="bi bi-wallet2 me-2"></i>
                    <strong>Saldo por cobrar:</strong> ${(parseFloat(clienteDetalle.saldo_cxc.saldo_total) || 0).toFixed(2)}
                    {parseFloat(clienteDetalle.saldo_cxc.saldo_vencido) > 0 && (
                      <span className="text-danger ms-2">(vencido: ${(parseFloat(clienteDetalle.saldo_cxc.saldo_vencido) || 0).toFixed(2)})</span>
                    )}
                  </div>
                )}

                {/* [Ficha 360] Ventas */}
                <h6 className="text-primary border-bottom pb-2 mb-3"><i className="bi bi-bag me-2"></i>Ventas ({clienteDetalle.ventas?.length || 0})</h6>
                {clienteDetalle.ventas?.length > 0 ? (
                  <div className="table-responsive"><table className="table table-sm">
                    <thead><tr><th>Código</th><th>N° Orden</th><th>Fecha</th><th>Total</th><th>Estado</th></tr></thead>
                    <tbody>
                      {(clienteDetalle.ventas || []).map(v => (
                        <tr key={v.id}>
                          <td><span className="badge bg-dark">{v.codigo}</span></td>
                          <td className="fw-bold text-primary">{v.numero_orden || '—'}</td>
                          <td>{v.created_at ? new Date(v.created_at).toLocaleDateString('es-EC') : '—'}</td>
                          <td>${(parseFloat(v.total) || 0).toFixed(2)}</td>
                          <td>{v.es_credito && parseFloat(v.saldo_pendiente) > 0
                            ? <span className="badge bg-warning text-dark">crédito ${(parseFloat(v.saldo_pendiente) || 0).toFixed(2)}</span>
                            : <span className="badge bg-success">{v.estado_entrega === 'pendiente_stock' ? 'pend. stock' : 'ok'}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                ) : (<p className="text-muted">Sin ventas registradas</p>)}

                {/* [Ficha 360] Órdenes de trabajo */}
                <h6 className="text-primary border-bottom pb-2 mb-3"><i className="bi bi-eyeglasses me-2"></i>Órdenes de trabajo ({clienteDetalle.ordenes_trabajo?.length || 0})</h6>
                {clienteDetalle.ordenes_trabajo?.length > 0 ? (
                  <div className="table-responsive"><table className="table table-sm">
                    <thead><tr><th>Código</th><th>N° Orden</th><th>Fecha</th><th>Estado</th></tr></thead>
                    <tbody>
                      {(clienteDetalle.ordenes_trabajo || []).map(o => (
                        <tr key={o.id}>
                          <td><span className="badge bg-primary">{o.codigo}</span></td>
                          <td className="fw-bold text-primary">{o.numero_orden || '—'}</td>
                          <td>{o.created_at ? new Date(o.created_at).toLocaleDateString('es-EC') : '—'}</td>
                          <td><span className="badge bg-secondary">{o.estado}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                ) : (<p className="text-muted">Sin órdenes de trabajo</p>)}

                {/* [Ficha 360] Atenciones / servicios */}
                {clienteDetalle.atenciones?.length > 0 && (
                  <>
                    <h6 className="text-primary border-bottom pb-2 mb-3"><i className="bi bi-clipboard2-pulse me-2"></i>Atenciones / servicios ({clienteDetalle.atenciones.length})</h6>
                    <div className="table-responsive"><table className="table table-sm">
                      <thead><tr><th>Código</th><th>N° Orden</th><th>Fecha</th><th>Total</th></tr></thead>
                      <tbody>
                        {clienteDetalle.atenciones.map(a => (
                          <tr key={a.id}>
                            <td><span className="badge bg-info text-dark">{a.codigo}</span></td>
                            <td className="fw-bold text-primary">{a.numero_orden || '—'}</td>
                            <td>{a.created_at ? new Date(a.created_at).toLocaleDateString('es-EC') : '—'}</td>
                            <td>${(parseFloat(a.total) || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                  </>
                )}

                {/* [Ficha 360] Certificados */}
                {clienteDetalle.certificados?.length > 0 && (
                  <>
                    <h6 className="text-primary border-bottom pb-2 mb-3"><i className="bi bi-award me-2"></i>Certificados ({clienteDetalle.certificados.length})</h6>
                    <div className="table-responsive"><table className="table table-sm">
                      <thead><tr><th>Código</th><th>Tipo</th><th>Fecha</th></tr></thead>
                      <tbody>
                        {clienteDetalle.certificados.map(ce => (
                          <tr key={ce.id}>
                            <td><span className="badge bg-light text-dark">{ce.codigo}</span></td>
                            <td>{ce.tipo}</td>
                            <td>{ce.created_at ? new Date(ce.created_at).toLocaleDateString('es-EC') : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                  </>
                )}

                {/* Tracking de usuario */}
                {(clienteDetalle.creado_por_nombre || clienteDetalle.modificado_por_nombre) && (
                  <div className="border-top pt-2 mt-3 d-flex flex-wrap gap-3">
                    {clienteDetalle.creado_por_nombre && (
                      <small className="text-muted">
                        <i className="bi bi-person-plus me-1"></i>Creado por: {clienteDetalle.creado_por_nombre}
                      </small>
                    )}
                    {clienteDetalle.modificado_por_nombre && (
                      <small className="text-muted">
                        <i className="bi bi-pencil me-1"></i>Modificado por: {clienteDetalle.modificado_por_nombre}
                      </small>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => { setShowDetalle(false); navigate(`/historia-clinica/nueva?cliente=${clienteDetalle.id}`); }}>
                  <i className="bi bi-file-medical me-2"></i>Nueva Historia Clínica
                </button>
                <button className="btn btn-secondary" onClick={() => setShowDetalle(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientes;
